import { useEffect, useRef, useState } from 'react'
import { Empty, Message } from '@arco-design/web-react'
import { IconCheck, IconCopy } from '@arco-design/web-react/icon'
import { useTranslation } from 'react-i18next'
import type { CodeBlock, Step, TutorialBadgeColor, TutorialTool } from '@/pages/Docs/tutorialData'
import TutorialToolLogo from './TutorialToolLogo'

const badgeColorMap: Record<TutorialBadgeColor, string> = {
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  violet: 'bg-violet-100 text-violet-700 border-violet-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
}

type TutorialCodeBlockProps = {
  id: string
  block: CodeBlock
  copiedId: string | null
  onCopy: (id: string, code: string) => void
}

function TutorialCodeBlock({ id, block, copiedId, onCopy }: TutorialCodeBlockProps) {
  const { t } = useTranslation()

  return (
    <div className="docs-code-block mt-3 max-w-full min-w-0" data-lang={block.language || 'bash'}>
      <div className="docs-code-header">
        <span className="min-w-0 truncate">{block.label}</span>
        <button type="button" className={`docs-code-copy shrink-0 ${copiedId === id ? 'is-copied' : ''}`} onClick={() => onCopy(id, block.code)}>
          {copiedId === id ? (
            <>
              <IconCheck /> {t('common.copied')}
            </>
          ) : (
            <>
              <IconCopy /> {t('common.copy')}
            </>
          )}
        </button>
      </div>
      <pre>
        <code>{block.code}</code>
      </pre>
    </div>
  )
}

type StepArticleProps = {
  step: Step
  index: number
  stepColor: string
  toolId: string
  copiedId: string | null
  onCopy: (id: string, code: string) => void
}

function StepArticle({ step, index, stepColor, toolId, copiedId, onCopy }: StepArticleProps) {
  const stepNumber = String(index + 1).padStart(2, '0')

  return (
    <article className="docs-step-card">
      <div className="flex min-w-0 items-start gap-3">
        <span className={`docs-step-badge ${stepColor}`}>{stepNumber}</span>
        <div className="min-w-0 flex-1 space-y-2 overflow-hidden">
          <h4 className="text-[15px] font-semibold text-[#111827] break-words">{step.title}</h4>
          {step.description && (
            <p className="text-sm leading-6 text-[#475569] break-words">{step.description}</p>
          )}

          {step.codes?.map((codeBlock, codeIndex) => (
            <TutorialCodeBlock
              key={`${toolId}-${index}-${codeBlock.label}-${codeIndex}`}
              id={`${toolId}-${index}-${codeIndex}`}
              block={codeBlock}
              copiedId={copiedId}
              onCopy={onCopy}
            />
          ))}

          {step.notes && step.notes.length > 0 && (
            <div className="docs-notes-callout">
              <ul>
                {step.notes.map((note) => (
                  <li key={note} className="break-words">
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

type ToolSectionProps = {
  tool: TutorialTool
  copiedId: string | null
  onCopy: (id: string, code: string) => void
  stepLabels: string[]
}

function ToolSection({ tool, copiedId, onCopy, stepLabels }: ToolSectionProps) {
  const [activePlatform, setActivePlatform] = useState(tool.platforms[0]?.id ?? 'all')

  useEffect(() => {
    setActivePlatform(tool.platforms[0]?.id ?? 'all')
  }, [tool.id, tool.platforms])

  const steps =
    tool.platforms.length > 0
      ? (tool.platforms.find((platform) => platform.id === activePlatform)?.steps ?? tool.platforms[0].steps)
      : tool.steps

  return (
    <div className="docs-tool-body">
      <div className="docs-tool-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="docs-tool-title-row">
              <span className="docs-tool-title-icon" aria-hidden>
                <TutorialToolLogo toolId={tool.id} size={28} />
              </span>
              <h2 className={`docs-tool-title ${tool.textColor}`}>{tool.name}</h2>
            </div>
            <p className="mt-1.5 text-sm leading-6 text-[#475569] break-words">{tool.tagline}</p>
          </div>
          <span
            className={`inline-flex shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${badgeColorMap[tool.badgeColor]}`}
          >
            {tool.badgeText}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {stepLabels.map((item, itemIndex) => (
            <div
              key={`${tool.id}-${item}`}
              className="rounded-xl border border-white/70 bg-white/80 px-3 py-2 text-center text-xs font-semibold text-[#334155] break-words"
            >
              <span className="mr-1 text-[11px] text-indigo-500">0{itemIndex + 1}</span>
              {item}
            </div>
          ))}
        </div>
      </div>

      {tool.platforms.length > 0 && (
        <div className="docs-platform-tabs">
          <div className="-mx-0.5 flex flex-wrap items-center gap-2 overflow-x-auto pb-0.5">
            {tool.platforms.map((platform) => (
              <button
                key={`${tool.id}-${platform.id}`}
                type="button"
                className={`docs-platform-tab ${activePlatform === platform.id ? 'is-active' : ''}`}
                onClick={() => setActivePlatform(platform.id)}
              >
                {platform.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 space-y-4">
        {steps.map((step, index) => (
          <StepArticle
            key={`${tool.id}-${step.title}-${index}`}
            step={step}
            index={index}
            stepColor={tool.stepColor}
            toolId={tool.id}
            copiedId={copiedId}
            onCopy={onCopy}
          />
        ))}
      </div>
    </div>
  )
}

export interface TutorialContentProps {
  tool: TutorialTool | undefined
}

export default function TutorialContent({ tool }: TutorialContentProps) {
  const { t } = useTranslation()
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const copyTimerRef = useRef<number | null>(null)

  const stepLabels = [
    t('landing.tutorials.stepRegister'),
    t('landing.tutorials.stepEndpoint'),
    t('landing.tutorials.stepCall'),
  ]

  const handleCopy = async (id: string, code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedId(id)
      Message.success(t('landing.tutorials.codeCopied'))
    } catch {
      Message.error(t('common.copy'))
      return
    }

    if (copyTimerRef.current) {
      window.clearTimeout(copyTimerRef.current)
    }

    copyTimerRef.current = window.setTimeout(() => {
      setCopiedId(null)
    }, 2000)
  }

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) {
        window.clearTimeout(copyTimerRef.current)
      }
    }
  }, [])

  if (!tool) {
    return <Empty description={t('docs.empty')} />
  }

  return (
    <ToolSection
      key={tool.id}
      tool={tool}
      copiedId={copiedId}
      onCopy={handleCopy}
      stepLabels={stepLabels}
    />
  )
}
